<template>
  <vue-scroll class="view box grow flex gaps grid-2">
    <div class="box grow login-container">
      <div class="grid-logo">
        <div class="logo-box align-vertical">
          <div class="logo-container align-vertical-middle">
            <div class="mt-68">
              <img class="logo-letter" src="/static/images/horizontal_logo_white.svg" alt="logo" />
              <div class="logo-desc">Autonomous Detection and Response</div>
            </div>
          </div>
        </div>
      </div>
      <el-form
        class="flex align-center grid-content login-page align-vertical"
        :model="userForm"
        :rules="userRules"
        ref="signupRef"
      >
        <div class="box form-wrapper align-vertical-middle">
          <div class="form-box">
            <div class="sign-in small-font h3">
              <span class="mr-8">Have an account?</span>
              <router-link to="/login" slot="title"> <el-button plain>SIGN IN</el-button> </router-link>
            </div>
            <div class="desc small-font mt-68 body1">
              <h3>Activate your account</h3>
              <div class="mt-16">Welcome to confluera. Let's get you all set up so you can get started.</div>
            </div>
            <float-label class="styled">
              <input class="button1" type="email" placeholder="Email Address" v-model="email" disabled />
            </float-label>

            <el-form-item class="styled" label="Password" prop="password">
              <el-input class="button1" type="password" v-model="userForm.password" autocomplete="off"></el-input>
              <el-progress
                class="password-strength-meter"
                :percentage="userForm.passwordStrengthScore"
                :color="userForm.passwordStrengthColor"
                :show-text="false"
              ></el-progress>
            </el-form-item>

            <el-form-item class="styled" label="Confirm Password" prop="confirmPassword">
              <el-input
                type="password"
                class="button1"
                v-model="userForm.confirmPassword"
                autocomplete="off"
                v-on:keyup.enter="activateAccount()"
              ></el-input>
            </el-form-item>

            <div v-if="accountActivationMessage" class="error">
              <label>{{ accountActivationMessage }}</label>
            </div>
            <div class="company-desc small-font">
              By clicking the button, you agree to our terms of services and have <br />read and acknowledge our privacy
              statement.
            </div>

            <div class="flex text-center center mt-8 mb-8">
              <el-button plain size="small" @click="activateAccount" class="login-btn">Activate Account</el-button>
            </div>
          </div>
        </div>
      </el-form>
    </div>
  </vue-scroll>
</template>

<script>
import { mapGetters } from "vuex";
import {
  SIGNUP_TOKEN_REQUEST,
  AUTH_RESET_REQUEST_CLEAR,
  AUTH_SUCCESS,
  AUTH_TOKEN_REFRESH_INTERVAL
} from "@/store/modules/auth/auth.type";

import { FETCH_NAV, UPDATE_NAV, NAV_SUCCESS } from "@/store/modules/navigation/nav.type";
import { TRAIL_SUMMARY_REQ } from "@/store/modules/trails/trailsinfo.type";
import { USER_PROFILE, USER_PERMISSION_SET } from "@/store/modules/user/user.type";

import { PasswordScoreColorEnum } from "@/constants/password-score.constants";
import { validatePassword } from "@/utilities/user/validation";
import { getPasswordStrengthColor } from "@/utilities/passwordstrength.helper";

export default {
  name: "SignUp",
  data() {
    return {
      userForm: {
        password: "",
        confirmPassword: "",
        passwordStrengthScore: 0,
        passwordStrengthColor: PasswordScoreColorEnum[0],
        error: false
      },
      userRules: {
        password: [
          {
            required: true,
            min: 8,
            max: 64,
            trigger: "change",
            validator: (rule, value, callback) => {
              let validPassword = validatePassword(value);
              let passwordStrengthMeta = getPasswordStrengthColor(validPassword.score);
              this.userForm.passwordStrengthScore = passwordStrengthMeta.percentage;
              this.userForm.passwordStrengthColor = passwordStrengthMeta.color;
              if (validPassword.score > 2) {
                if (this.userForm.confirmPassword !== "") {
                  this.$refs["signupRef"].validateField("confirmPassword");
                }
                callback();
              } else {
                callback(validPassword.error);
              }
            }
          }
        ],
        confirmPassword: [
          {
            required: true,
            trigger: "blur",
            validator: (rule, value, callback) => {
              if (value === "") {
                callback(new Error("Please input the password again"));
              } else if (value !== this.userForm.password) {
                callback(new Error("Passwords did not match!"));
              } else {
                callback();
              }
            }
          }
        ]
      }
    };
  },
  methods: {
    activateAccount() {
      this.$refs["signupRef"].validate(valid => {
        if (valid) {
          this.$store
            .dispatch(SIGNUP_TOKEN_REQUEST, {
              password: this.userForm.password,
              token: this.token
            })
            .then(
              jwt => {
                this.$store.dispatch(AUTH_SUCCESS, jwt);

                // GET user profile after login (roles, permissions)
                // GET system summary - directs to different dashboard view depending on attack trails detected
                Promise.all([
                  this.$store.dispatch(USER_PROFILE, { email: this.email }),
                  this.$store.dispatch(TRAIL_SUMMARY_REQ),
                  this.$store.dispatch(FETCH_NAV)
                ]).then(
                  values => {
                    let profile = values[0];
                    let summaryInfo = values[1];

                    let permissions = _.flatten(_.map(profile.roles, "permissions"));
                    this.$store.dispatch(USER_PERMISSION_SET, permissions);
                    this.$store.dispatch(UPDATE_NAV, permissions);
                    this.$store.dispatch(NAV_SUCCESS);

                    // set interval to refresh access tokens
                    this.$store.dispatch(AUTH_TOKEN_REFRESH_INTERVAL);

                    // direct user to landing page
                    // main system health dashboard
                    if (this.insightsEnabled) {
                      this.$router.push("/home");
                    } else if (summaryInfo.numActiveTrails > 0) {
                      // land on attack trail dashboard if any exists
                      this.$router.push({ name: "dashboard-trails" });
                    } else if (summaryInfo.numPrivilegedTrails > 0) {
                      // else direct to privilege trail dashboard
                      this.$router.push({ name: "dashboard-privileged" });
                    } else {
                      this.$router.push({ name: "dashboard-agents" });
                    }
                  },
                  errMsg => {
                    if (this.insightsEnabled) {
                      this.$router.push("/home");
                    } else {
                      this.$router.push({ name: "dashboard-trails" });
                    }
                  }
                );
              },
              errMsg => {
                this.userForm.error = true;
                console.log(errMsg);
              }
            );
        }
      });
    }
  },
  created() {
    this.$store.dispatch(AUTH_RESET_REQUEST_CLEAR);
    this.email = this.$route.params.email;
    this.token = this.$route.params.token;
  },
  computed: {
    ...mapGetters(["accountActivationStatus", "accountActivationMessage", "insightsEnabled"])
  }
};
</script>

<style scoped>
@import "assets/styles/_variables.css";
@import "assets/styles/global_classes.css";
@import "assets/styles/text.css";
@import "assets/styles/login.css";

.layout-container .container .view {
  padding: 0px;
  padding-bottom: 0px;
}

.layout-container .container .view {
  padding: 0px;
  padding-bottom: 0px;
}

.error {
  text-align: center;
  color: var(--text-color-danger);
}

.company-desc {
  font-size: 0.8em;
  color: black;
  margin-top: 24px;
}

.desc {
  font-size: 0.8em;
  color: black;
  margin-top: 68px;
}

>>> .el-loading-mask {
  background-color: white;
}

.logo-container {
  > div {
    height: 362px; /* Match height of right grid content */
  }
}

.password-strength-meter {
  margin-top: 10px;
}
</style>



// WEBPACK FOOTER //
// src/views/login/SignUp.vue